import { DeleteFilled } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
  COMPANY_CLAIM_REQUEST_SETTLE,
  COMPANY_PROFILE_OWNER_REMOVE,
  CompanyClaim,
  ErrorResponse,
  GET_COMPANY,
  GET_COMPANY_CLAIM_REQUESTS,
} from '@frontend/data-access';
import { UserAvatar } from '@frontend/shared/ui';
import { getLinkLabel } from '@frontend/shared/util';
import { App, Button, Collapse, Divider, Spin, Typography } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useState } from 'react';

function EditProfileManagerForm({
  companyId,
  onCancel,
}: {
  companyId: number;
  onCancel: () => void;
}) {
  const { notification } = App.useApp();
  const { t } = useTranslation('common');
  const [activeAction, setActiveAction] = useState<'REJECTED' | 'APPROVED'>();
  const { data, loading } = useQuery<{ listCompanyClaims: CompanyClaim[] }>(
    GET_COMPANY_CLAIM_REQUESTS,
    {
      variables: { companyId: companyId },
    }
  );
  const handleCompleted = () => {
    setActiveAction(undefined);
    notification.success({
      message: t('claim_request_updated'),
    });
  };
  const handleError = (error: ErrorResponse) => {
    notification.error({
      message: error.message,
    });
  };
  const [settle, { loading: updateLoading }] = useMutation(
    COMPANY_CLAIM_REQUEST_SETTLE,
    {
      onCompleted: handleCompleted,
      refetchQueries: [
        { query: GET_COMPANY_CLAIM_REQUESTS, variables: { companyId } },
        { query: GET_COMPANY, variables: { id: companyId } },
      ],
      onError: handleError,
    }
  );
  const handleRemoveCompleted = () => {
    notification.success({
      message: t('profile_manager_removed'),
    });
  };
  const [remove, { loading: deleteLoading }] = useMutation(
    COMPANY_PROFILE_OWNER_REMOVE,
    {
      onCompleted: handleRemoveCompleted,
      refetchQueries: [
        { query: GET_COMPANY_CLAIM_REQUESTS, variables: { companyId } },
        { query: GET_COMPANY, variables: { id: companyId } },
      ],
    }
  );

  const handleReject = (claimId: number) => {
    setActiveAction('REJECTED');
    settle({
      variables: {
        input: {
          companyId: companyId,
          claimId: claimId,
          companyClaimStatus: 'REJECTED',
        },
      },
    });
  };

  const handleApprove = (claimId: number) => {
    setActiveAction('APPROVED');
    settle({
      variables: {
        input: {
          companyId: companyId,
          claimId: claimId,
          companyClaimStatus: 'APPROVED',
        },
      },
    });
  };
  const handleRemove = (userId: number) => {
    remove({
      variables: {
        input: {
          companyId: companyId,
          userId: userId,
        },
      },
    });
  };

  const RequestItem = ({
    claim,
    index,
  }: {
    claim: CompanyClaim;
    index: number;
  }) => (
    <div key={index} className="flex gap-4 items-center justify-between">
      <div className="flex flex-row gap-2 items-center">
        <UserAvatar size={34} user={claim.user} />
        <div className="grid">
          <Typography.Text className="!font-medium !text-gray-800 line-clamp-1">
            {claim.user.fullName}
          </Typography.Text>
          <span className="font-normal text-gray-700">
            {claim.user?.role
              ? getLinkLabel(claim.user.role.toLowerCase())
              : ''}
          </span>
        </div>
      </div>
      <div className="flex gap-2 flex-nowrap">
        <Button
          loading={updateLoading && activeAction === 'APPROVED'}
          shape="round"
          onClick={() => handleApprove(claim?.id)}
        >
          {t('approve')}
        </Button>
        <Button
          loading={updateLoading && activeAction === 'REJECTED'}
          danger
          shape="round"
          onClick={() => handleReject(claim?.id)}
        >
          {t('reject')}
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="text-center">
        <Spin />
      </div>
    );
  }

  return (
    <div className="pt-2">
      <h5 className="font-medium text-gray-900 mb-4">
        {t('institution_profile_managers')}
      </h5>
      <div className="space-y-2">
        {data?.listCompanyClaims
          ?.filter((claim) => claim?.status === 'APPROVED')
          .map((claim, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex flex-row gap-2 items-center">
                <UserAvatar size={34} user={claim.user} />
                <div className="grid">
                  <span className="font-medium text-gray-800">
                    {claim.user.fullName}
                  </span>
                  <span className="font-normal text-gray-700">
                    {claim.user?.role
                      ? getLinkLabel(claim.user.role.toLowerCase())
                      : ''}
                  </span>
                </div>
              </div>
              <div className="space-x-2">
                <Button
                  loading={deleteLoading}
                  icon={<DeleteFilled />}
                  shape="circle"
                  onClick={() => handleRemove(claim?.user?.id)}
                />
              </div>
            </div>
          ))}
      </div>
      <Divider />
      <h5 className="font-medium text-gray-900 mb-4">{t('requests')}</h5>{' '}
      <p className="font-normal text-gray-700">
        {t('profile_manager_requests')}
      </p>
      <Collapse ghost>
        {data?.listCompanyClaims
          ?.filter((claim) => claim?.status === 'PENDING')
          .map((claim, index) => (
            <Collapse.Panel
              header={<RequestItem claim={claim} index={index} />}
              key={index}
            >
              <div className="space-y-3">
                <div>
                  <Typography.Text>
                    {t('institution_name')}: {claim?.name}
                  </Typography.Text>{' '}
                  <div>
                    {t('work_email')}: {claim?.email}
                  </div>{' '}
                  <div>
                    {t('phone_number')}: {claim?.phone}
                  </div>{' '}
                </div>
                <div>
                  <div>{t('description')}:</div>
                  <div>{claim?.reason}</div>
                </div>
              </div>
            </Collapse.Panel>
          ))}
      </Collapse>
    </div>
  );
}

export default EditProfileManagerForm;
